/**
 * Chilean RUT Validation Utilities
 * RUT (Rol Único Tributario) - Chilean tax identification number
 * Format: XX.XXX.XXX-Y where Y is verification digit (0-9 or K)
 */

/**
 * Clean RUT string removing dots and dashes
 */
export const cleanRut = (rut) => {
  if (!rut) return '';
  return rut.replace(/[.-]/g, '').toUpperCase();
};

/**
 * Format RUT with dots and dash
 * @param {string} rut - Raw RUT string
 * @returns {string} Formatted RUT (XX.XXX.XXX-X)
 */
export const formatRut = (rut) => {
  if (!rut) return '';
  
  // Clean the RUT first
  let cleaned = cleanRut(rut);
  
  // Remove any non-alphanumeric except K
  cleaned = cleaned.replace(/[^0-9K]/gi, '');
  
  if (cleaned.length < 2) return cleaned;
  
  // Separate body from verification digit
  const body = cleaned.slice(0, -1);
  const dv = cleaned.slice(-1);
  
  // Format body with dots (from right to left)
  let formatted = '';
  for (let i = body.length - 1, count = 0; i >= 0; i--, count++) {
    if (count > 0 && count % 3 === 0) {
      formatted = '.' + formatted;
    }
    formatted = body[i] + formatted;
  }
  
  return `${formatted}-${dv}`;
};

/**
 * Calculate RUT verification digit
 * @param {string} rutBody - RUT without verification digit
 * @returns {string} Verification digit (0-9 or K)
 */
export const calculateVerificationDigit = (rutBody) => {
  if (!rutBody) return '';
  
  // Clean and get only numbers
  const body = rutBody.replace(/\D/g, '');
  if (!body) return '';
  
  // Sequence multiplier: 2,3,4,5,6,7,2,3,4,5,6,7...
  let sum = 0;
  let multiplier = 2;
  
  // Process from right to left
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }
  
  // Calculate verification digit
  const remainder = sum % 11;
  const digit = 11 - remainder;
  
  if (digit === 11) return '0';
  if (digit === 10) return 'K';
  return digit.toString();
};

/**
 * Validate Chilean RUT
 * @param {string} rut - RUT to validate (any format)
 * @returns {object} { isValid: boolean, error?: string }
 */
export const validateRut = (rut) => {
  if (!rut) {
    return { isValid: false, error: 'RUT requerido' };
  }
  
  // Clean the RUT
  const cleaned = cleanRut(rut);
  
  // Check minimum length (at least 8 digits + 1 verification digit)
  if (cleaned.length < 8) {
    return { isValid: false, error: 'RUT muy corto' };
  }
  
  // Check maximum length (12 digits + 1 verification digit for companies)
  if (cleaned.length > 12) {
    return { isValid: false, error: 'RUT muy largo' };
  }
  
  // Separate body and verification digit
  const body = cleaned.slice(0, -1);
  const providedDv = cleaned.slice(-1);
  
  // Check body is all numbers
  if (!/^\d+$/.test(body)) {
    return { isValid: false, error: 'RUT contiene caracteres inválidos' };
  }
  
  // Check verification digit is number or K
  if (!/^[0-9K]$/.test(providedDv)) {
    return { isValid: false, error: 'Dígito verificador inválido' };
  }
  
  // Calculate expected verification digit
  const expectedDv = calculateVerificationDigit(body);
  
  // Compare
  if (providedDv !== expectedDv) {
    return { 
      isValid: false, 
      error: `Dígito verificador incorrecto (esperado: ${expectedDv})` 
    };
  }
  
  return { isValid: true };
};

/**
 * Get RUT type based on number range
 * @param {string} rut - RUT to check
 * @returns {string} Type: 'persona' | 'empresa' | 'extranjero'
 */
export const getRutType = (rut) => {
  const cleaned = cleanRut(rut);
  const body = cleaned.slice(0, -1);
  const number = parseInt(body);
  
  if (isNaN(number)) return 'desconocido';
  
  // Chilean RUT ranges (approximate):
  // 1-50.000.000: Natural persons
  // 50.000.000-99.999.999: Foreign investors & special entities
  // 60.000.000-69.999.999: Companies (older)
  // 70.000.000-79.999.999: Companies (modern)
  // 76.000.000-77.000.000: SpA & newer companies
  // 96.000.000+: Large corporations & banks
  
  if (number < 50000000) {
    return 'persona';
  } else if (number >= 50000000 && number < 60000000) {
    return 'extranjero';
  } else {
    return 'empresa';
  }
};

export default {
  cleanRut,
  formatRut,
  calculateVerificationDigit,
  validateRut,
  getRutType
};
